import { useTheme, styled } from '@mui/material/styles';
import React, { useEffect, useState, useMemo } from 'react';
import { Container, useMediaQuery, Box, Grid2 } from '@mui/material';
import { responsiveFontSizes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getGroupsForUser, getValidPeriodsForGroup, getUserInfo } from '../apis/apis'
import Questionnaire from '../components/Questionnaire.jsx';
import EnhancedNavbar from '../components/EnhancedNavbar.jsx';
import MembersList from '../components/MembersList.jsx';
import { jwtDecode } from 'jwt-decode';

const HomeContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: '100px', // Increased padding to account for the navbar on small screens
  width: '100%',
  maxWidth: '100%',
  minHeight: 'calc(100vh - 64px)', // Ensure container takes at least full viewport height minus navbar
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    paddingTop: '100px', // Ensure enough space on small screens
    paddingLeft: 0,
    paddingRight: 0,
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
    paddingTop: '100px',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '1280px',
  },
}));

export default function Home() {
  let theme = useTheme();
  theme = responsiveFontSizes(theme);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // User info state
  const [userInfo, setUserInfo] = useState(null);

  // Group-related state
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);

  // Period-related state
  const [validPeriods, setValidPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [periodsCache, setPeriodsCache] = useState({});
  const [isCurrentPeriod, setIsCurrentPeriod] = useState(true);

  // Get current period data using useMemo to avoid recalculations
  const currentPeriodData = useMemo(() => {
    return validPeriods.find(period => period.weekStart === selectedPeriod);
  }, [validPeriods, selectedPeriod]);

  // Update isCurrentPeriod whenever currentPeriodData changes
  useEffect(() => {
    if (currentPeriodData) {
      setIsCurrentPeriod(currentPeriodData.isCurrentPeriod);
    }
  }, [currentPeriodData]);

  // Reset selected member only when group changes
  useEffect(() => {
    setSelectedMember(null);
  }, [currentGroup]);

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfoData = await getUserInfo();
        setUserInfo(userInfoData);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    if (localStorage.getItem("token")) {
      fetchUserInfo();
    }
  }, []);

  // Initial groups fetch
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const fetchedGroups = await getGroupsForUser();
        setGroups(fetchedGroups);
        if (fetchedGroups.length > 0) {
          let groupToSet = '';
          // check if last opened group is in the fetched groups
          if (localStorage.getItem("lastOpenedGroup") && fetchedGroups.find(group => group.id === localStorage.getItem("lastOpenedGroup"))) {
            groupToSet = localStorage.getItem("lastOpenedGroup");
          } else {
            groupToSet = fetchedGroups[0].id;
          }
          // set the current group
          setCurrentGroup(groupToSet);
          // set the group members
          setGroupMembers(fetchedGroups.find(group => group.id === groupToSet).users);
        }
      } catch (error) {
        if (error.message.includes("401")) {
          navigate('/signin');
        } else if (error.message.includes("403")) {
          navigate('/verify_email');
        } else if (error.message.includes("400")) {
          navigate('/signin');
        }
      }
    };
    fetchGroups();
  }, [navigate]);

  // Handle group selection
  const handleGroupChange = (groupId) => {
    setCurrentGroup(groupId);
    const selectedGroup = groups.find(group => group.id === groupId);
    if (selectedGroup) {
      setGroupMembers(selectedGroup.users);
    }
    // set the current group
    localStorage.setItem("lastOpenedGroup", groupId);
  };

  // Fetch periods when group changes
  useEffect(() => {
    const fetchValidPeriods = async () => {
      if (!currentGroup) return;

      try {
        // Use cached periods if available
        if (periodsCache[currentGroup]) {
          const periods = periodsCache[currentGroup];
          setValidPeriods(periods);
          setSelectedPeriod(periods[0].weekStart);
          
          // If it's not the current period, select the first member
          if (!periods[0].isCurrentPeriod && groupMembers.length > 0) {
            setSelectedMember(groupMembers[0]);
          }
          return;
        }

        // Fetch new periods if not cached
        const periods = await getValidPeriodsForGroup(currentGroup);
        
        // Update cache and state
        setPeriodsCache(prev => ({
          ...prev,
          [currentGroup]: periods
        }));
        
        setValidPeriods(periods);
        if (periods.length > 0) {
          setSelectedPeriod(periods[0].weekStart);
          // If it's not the current period, select the first member
          if (!periods[0].isCurrentPeriod && groupMembers.length > 0) {
            setSelectedMember(groupMembers[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching valid periods:', error);
      }
    };

    fetchValidPeriods();
  }, [currentGroup, periodsCache, groupMembers]);

  // Update selected member when period changes
  const handlePeriodChange = (periodStart) => {
    setSelectedPeriod(periodStart);
    const selectedPeriodData = validPeriods.find(p => p.weekStart === periodStart);
    
    if (selectedPeriodData?.isCurrentPeriod) {
      // Clear selected member for current period
      setSelectedMember(null);
    } else if (!selectedMember && groupMembers.length > 0) {
      // Auto-select first member for past periods if none selected
      setSelectedMember(groupMembers[0]);
    }
  };

  const handleMemberSelect = (member) => {
    if (!isCurrentPeriod) {
      setSelectedMember(member);
    }
  };

  const refreshGroupsAndSetCurrentGroup = async (currentGroupId = null) => {
    const fetchedGroups = await getGroupsForUser();
    setGroups(fetchedGroups);
    if (currentGroupId) {
      setCurrentGroup(currentGroupId);
      const currentGroup = fetchedGroups.find(group => group.id === currentGroupId);
      if (currentGroup) {
        setGroupMembers(currentGroup.users);
      }
    }
  }

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/signin');
        return;
      }

      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp < currentTime) {
          localStorage.removeItem('token');
          navigate('/signin');
        }
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/signin');
      }
    };

    checkAuth();
  }, [navigate]);

  // Only show members list in navbar on mobile
  const showMembersListInNavbar = isMobile;

  return (
    <>
      <EnhancedNavbar 
        userInfo={userInfo}
        groups={groups}
        currentGroup={currentGroup}
        refreshGroups={refreshGroupsAndSetCurrentGroup}
        onGroupChange={handleGroupChange}
        validPeriods={validPeriods}
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        groupMembers={groupMembers}
        onMemberSelect={handleMemberSelect}
        selectedMember={selectedMember}
        isCurrentPeriod={isCurrentPeriod}
        renderMembersListInNavbar={showMembersListInNavbar}
      />
      
      <HomeContainer>
        <Grid2 container spacing={0} size={{ xs: 0, md: 3 }} justifyContent={'center'}>
          {/* Questionnaire - takes most of the width on desktop */}
          <Grid2 size={{ xs: 12, md: 9 }}>
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <Questionnaire
                selectedPeriod={selectedPeriod}
                currentGroup={currentGroup}
                selectedMember={selectedMember}
                isCurrentPeriod={isCurrentPeriod}
                groupMembers={groupMembers}
              />
            </Box>
          </Grid2>
          
          {/* Members List - always shown on desktop */}
          {!isMobile && (
            <Grid2 size={{ md: 3 }}>
              <Box sx={{ width: '100%', position: 'sticky', top: '90px' }}>
                <MembersList
                  groupMembers={groupMembers}
                  onMemberSelect={handleMemberSelect}
                  selectedMember={selectedMember}
                  isCurrentPeriod={isCurrentPeriod}
                  currentGroup={currentGroup}
                  refreshGroups={refreshGroupsAndSetCurrentGroup}
                />
              </Box>
            </Grid2>
          )}
        </Grid2>
      </HomeContainer>
    </>
  );
}